import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import {
  Plus, Minus, ShoppingCart, X, Search,
  Trash2, RefreshCw, ChevronLeft, Wine, UtensilsCrossed, ShoppingBag, Check,
  Banknote, StickyNote, BookOpen, ClipboardList, Send
} from 'lucide-react';
import ReceiptTicketModal from './LiveTables/ReceiptTicketModal';
import { useOrderData, getProductName } from '../../hooks/useOrderData';
import { useOrderCart } from '../../hooks/useOrderCart';
import { useProductSelection } from '../../hooks/useProductSelection';
import SelectionModal from './OrderPanelParts/SelectionModal';
import NoteModal from './OrderPanelParts/NoteModal';
import PaymentModal from './OrderPanelParts/PaymentModal';
import MesaSelector from './OrderPanelParts/MesaSelector';
import CartPanel from './OrderPanelParts/CartPanel';

const SECTION_TABS = {
  'barra': { icon: Wine, color: '#9333EA', label: 'Barra', bg: '#F3E8FF' },
  'cocina': { icon: UtensilsCrossed, color: '#EA580C', label: 'Cocina', bg: '#FFF7ED' },
  'tienda': { icon: ShoppingBag, color: '#0D9488', label: 'Tienda', bg: '#F0FDFA' }
};

const ManualOrderPanel = () => {
  const { mesas, products, groupedCategories, loading } = useOrderData();
  const {
    cart, addToCart, updateQuantity, updateItemPrice, addNoteToCartItem, clearCart,
    getCartTotal, getCartCount, handlePayment, handleSendToTable,
    sending, receiptData, setReceiptData
  } = useOrderCart();
  const {
    selectionModal, setSelectionModal,
    selectedOptions, selectedSupplements,
    productNeedsSelection, getOptionGroups, getGroupLabel, getRequiredCount,
    handleSelectOption, handleToggleSupplement,
    isSelectionComplete, handleConfirmSelection,
    openSelection
  } = useProductSelection(addToCart);

  const [selectedMesa, setSelectedMesa] = useState(null);
  const [selectedSection, setSelectedSection] = useState('barra');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [noteModal, setNoteModal] = useState(null);
  const [showMesaSelector, setShowMesaSelector] = useState(false);
  const [viewMode, setViewMode] = useState('pedido');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const sectionCats = groupedCategories[selectedSection];
    if (sectionCats && sectionCats.length > 0 && !sectionCats.includes(selectedCategory)) {
      setSelectedCategory(sectionCats[0]);
    }
  }, [selectedSection, groupedCategories, selectedCategory]);

  const filteredProducts = useMemo(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = [];
      Object.values(products).forEach(catProds => {
        catProds.forEach(p => {
          if (getProductName(p).toLowerCase().includes(query)) results.push(p);
        });
      });
      return results;
    }
    return selectedCategory && products[selectedCategory] ? products[selectedCategory] : [];
  }, [selectedCategory, products, searchQuery]);

  const onPay = (method) => {
    handlePayment(method, selectedMesa, () => {
      setShowPaymentModal(false);
      setShowCart(false);
    });
  };

  const onSendToTable = (mesa) => {
    handleSendToTable(mesa, () => {
      setShowCart(false);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  // Shared product card renderer
  const renderProductCard = (product, isDesktop = false) => {
    const cartItems = cart.filter(c => c.id === product.id);
    const totalInCart = cartItems.reduce((sum, c) => sum + c.quantity, 0);
    const needsSelection = productNeedsSelection(product);
    return (
      <button key={product.id} onClick={() => openSelection(product)}
        className={`relative ${isDesktop ? 'bg-white rounded-2xl p-5' : 'bg-white border rounded-xl p-3'} text-left transition-all active:scale-[0.97] ${isDesktop ? 'border' : ''} ${totalInCart > 0 ? `ring-2 ring-green-500 ${isDesktop ? 'bg-green-50/50 border-green-200' : 'bg-green-50'}` : `${isDesktop ? 'border-gray-200 hover:border-gray-300 hover:shadow-md' : ''}`}`}
        data-testid={`quick-product-${product.id}`}>
        {totalInCart > 0 && (
          <div className={`absolute ${isDesktop ? '-top-2.5 -right-2.5 w-7 h-7 text-sm' : '-top-2 -right-2 w-6 h-6 text-xs'} bg-green-500 text-white rounded-full flex items-center justify-center font-bold shadow-md`}>{totalInCart}</div>
        )}
        <p className={`font-${isDesktop ? 'semibold' : 'medium'} text-gray-900 line-clamp-2 ${isDesktop ? 'text-base mb-3 leading-snug' : 'text-sm mb-2'}`}>{getProductName(product)}</p>
        <div className="flex items-center justify-between">
          <p className={`text-green-600 font-bold ${isDesktop ? 'text-xl' : 'text-lg'}`}>{(Number(product.price) || 0).toFixed(2)}€</p>
          <div className="flex items-center gap-2">
            {needsSelection && <span className={`text-xs text-amber-600 bg-amber-50 ${isDesktop ? 'px-2 py-1 rounded-lg' : 'px-1.5 py-0.5 rounded'} font-medium`}>Opciones</span>}
            {isDesktop && (
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Plus size={22} className="text-green-600" />
              </div>
            )}
          </div>
        </div>
      </button>
    );
  };

  // Shared section tabs renderer
  const renderSectionTabs = (isMobileView = false) => (
    <div className={isMobileView ? 'grid grid-cols-3 gap-2' : 'flex gap-2 flex-shrink-0'}>
      {Object.entries(SECTION_TABS).map(([key, config]) => {
        const Icon = config.icon;
        const isSelected = selectedSection === key;
        const count = groupedCategories[key]?.length || 0;
        if (count === 0) return isMobileView ? <div key={key} /> : null;
        return isMobileView ? (
          <button key={key} onClick={() => setSelectedSection(key)}
            className={`py-3 px-2 rounded-xl flex flex-col items-center justify-center transition-all ${isSelected ? 'shadow-lg scale-105' : 'opacity-70'}`}
            style={{ backgroundColor: isSelected ? `${config.color}15` : '#F3F4F6', border: isSelected ? `2px solid ${config.color}` : '2px solid transparent' }}
            data-testid={`mobile-section-${key}`}>
            <Icon size={28} style={{ color: isSelected ? config.color : '#6B7280' }} />
            <span className="font-bold text-sm mt-1" style={{ color: isSelected ? config.color : '#6B7280' }}>{config.label}</span>
          </button>
        ) : (
          <button key={key} onClick={() => { setSelectedSection(key); setSearchQuery(''); }}
            className={`flex items-center gap-2.5 px-5 h-12 rounded-xl font-bold text-base transition-all active:scale-95 ${isSelected ? 'shadow-md text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            style={{ backgroundColor: isSelected ? config.color : undefined }}
            data-testid={`section-tab-${key}`}>
            <Icon size={22} />
            {config.label}
          </button>
        );
      })}
    </div>
  );

  // ==================== MOBILE VIEW ====================
  if (isMobile) {
    if (showCart) {
      return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
          <div className="bg-white border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
            <button onClick={() => setShowCart(false)} className="p-1"><ChevronLeft size={24} /></button>
            <h2 className="font-bold text-lg flex-1">Pedido</h2>
            {selectedMesa && <Badge className="bg-green-600 text-white">Mesa {selectedMesa}</Badge>}
          </div>
          <div className="flex-1 overflow-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <ShoppingCart size={56} className="mx-auto mb-3 opacity-40" />
                <p className="text-lg">Pedido vacío</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item, idx) => {
                  const itemKey = item.cartId || item.id;
                  const isOverridden = item.price_overridden && item.original_price != null && Number(item.original_price) !== Number(item.price);
                  const handleEditPrice = () => {
                    const current = Number(item.price).toFixed(2);
                    const input = window.prompt(`Precio unitario para "${item.name}" (€):`, current);
                    if (input == null) return;
                    const parsed = Number(String(input).replace(',', '.'));
                    if (!Number.isNaN(parsed) && parsed >= 0) {
                      updateItemPrice(itemKey, parsed);
                    }
                  };
                  return (
                  <div key={item.cartId || `${item.id}-${idx}`} className="bg-white rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-gray-900 text-lg flex-1 pr-2">{item.name}</h4>
                      <button onClick={() => updateQuantity(itemKey, -999)} className="p-2 text-red-500"><Trash2 size={20} /></button>
                    </div>
                    {item.optionsText && <p className="text-sm text-gray-500 mb-1">{item.optionsText}</p>}
                    {item.notes && <p className="text-sm text-amber-600 mb-2">Nota: {item.notes}</p>}
                    <div className="flex items-center justify-between text-sm mb-2">
                      <button onClick={handleEditPrice} className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg border ${isOverridden ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-700'}`} data-testid={`mobile-price-edit-${itemKey}`}>
                        PVP {Number(item.price).toFixed(2)}€ ✏️
                      </button>
                      {isOverridden && (
                        <button onClick={() => updateItemPrice(itemKey, item.original_price)} className="text-xs text-gray-400 line-through" data-testid={`mobile-price-reset-${itemKey}`}>
                          {Number(item.original_price).toFixed(2)}€
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 bg-gray-100 rounded-full p-1">
                        <button onClick={() => updateQuantity(itemKey, -1)} className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-95"><Minus size={20} /></button>
                        <span className="w-8 text-center font-bold text-xl">{item.quantity}</span>
                        <button onClick={() => updateQuantity(itemKey, 1)} className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-95"><Plus size={20} /></button>
                      </div>
                      <p className={`text-2xl font-bold ${isOverridden ? 'text-amber-600' : 'text-green-600'}`}>{(Number(item.price) * item.quantity).toFixed(2)}€</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          {cart.length > 0 && (
            <div className="bg-white border-t p-4 pb-24 space-y-4">
              <div className="rounded-xl p-3 bg-gray-50 border border-gray-200">
                <p className="font-medium text-sm mb-2 text-gray-600">Mesa (opcional):</p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  <button onClick={() => setSelectedMesa(null)}
                    className={`flex-shrink-0 px-3 h-11 rounded-lg flex items-center justify-center font-medium text-sm transition-all ${!selectedMesa ? 'bg-gray-700 text-white shadow-md' : 'bg-white text-gray-500 border border-gray-200'}`}>
                    Sin mesa
                  </button>
                  {mesas.map(mesa => (
                    <button key={mesa.numero} onClick={() => setSelectedMesa(mesa.numero)}
                      className={`flex-shrink-0 w-11 h-11 rounded-lg flex items-center justify-center font-bold transition-all ${selectedMesa === mesa.numero ? 'bg-green-500 text-white shadow-md' : 'bg-white text-gray-600 border border-gray-200'}`}>
                      {mesa.numero}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 text-lg">Total</span>
                <span className="text-4xl font-bold text-green-600">{getCartTotal().toFixed(2)}€</span>
              </div>
              {selectedMesa && (
                <Button onClick={() => onSendToTable(selectedMesa)} disabled={cart.length === 0 || sending} className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-lg font-bold disabled:opacity-50" data-testid="mobile-send-table-btn">
                  <Send size={22} className="mr-2" />ENVIAR A MESA {selectedMesa}
                </Button>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={clearCart} className="flex-1 h-14 text-lg">Vaciar</Button>
                <Button onClick={() => setShowPaymentModal(true)} disabled={cart.length === 0 || sending} className="flex-1 h-14 bg-green-600 hover:bg-green-700 text-lg font-bold disabled:opacity-50" data-testid="mobile-cobrar-btn">
                  <Banknote size={22} className="mr-2" />COBRAR
                </Button>
              </div>
            </div>
          )}
          <SelectionModal product={selectionModal} selectedOptions={selectedOptions} selectedSupplements={selectedSupplements}
            getOptionGroups={getOptionGroups} getGroupLabel={getGroupLabel} getRequiredCount={getRequiredCount}
            onSelectOption={handleSelectOption} onToggleSupplement={handleToggleSupplement}
            isComplete={isSelectionComplete()} onConfirm={handleConfirmSelection} onClose={() => setSelectionModal(null)} getProductName={getProductName} />
          <PaymentModal show={showPaymentModal} selectedMesa={selectedMesa} total={getCartTotal()} itemCount={getCartCount()} sending={sending} onPay={onPay} onClose={() => setShowPaymentModal(false)} />
          <NoteModal item={noteModal} onAddNote={addNoteToCartItem} onClose={() => setNoteModal(null)} />
          {receiptData && <ReceiptTicketModal saleData={receiptData} onClose={() => setReceiptData(null)} />}
        </div>
      );
    }

    // Mobile Main View
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="bg-white border-b sticky top-0 z-20">
          <div className="px-3 py-2 flex items-center justify-between">
            <h1 className="font-bold text-lg">Venta Rápida</h1>
            <button onClick={() => setShowCart(true)} className="relative p-2" data-testid="mobile-cart-btn">
              <ShoppingCart size={26} className="text-gray-700" />
              {getCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{getCartCount()}</span>
              )}
            </button>
          </div>
        </div>
        <div className="bg-white border-b px-2 py-3">{renderSectionTabs(true)}</div>
        <div className="bg-white border-b px-2 py-2 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {groupedCategories[selectedSection]?.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedCategory === cat ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600'}`}
                style={{ backgroundColor: selectedCategory === cat ? SECTION_TABS[selectedSection].color : undefined }}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white px-3 py-2 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input placeholder="Buscar..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-11 text-base rounded-xl bg-gray-50" />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-3 pb-24">
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map(product => renderProductCard(product, false))}
          </div>
          {filteredProducts.length === 0 && <div className="text-center py-12 text-gray-400"><p>No hay productos</p></div>}
        </div>
        {cart.length > 0 && (
          <button onClick={() => setShowCart(true)}
            className="fixed bottom-20 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-4 rounded-full shadow-xl flex items-center gap-3 z-30 active:scale-95">
            <ShoppingCart size={24} />
            <span className="font-bold text-lg">{getCartTotal().toFixed(2)}€</span>
            <span className="bg-white text-green-600 w-8 h-8 rounded-full flex items-center justify-center font-bold">{getCartCount()}</span>
          </button>
        )}
        <SelectionModal product={selectionModal} selectedOptions={selectedOptions} selectedSupplements={selectedSupplements}
          getOptionGroups={getOptionGroups} getGroupLabel={getGroupLabel} getRequiredCount={getRequiredCount}
          onSelectOption={handleSelectOption} onToggleSupplement={handleToggleSupplement}
          isComplete={isSelectionComplete()} onConfirm={handleConfirmSelection} onClose={() => setSelectionModal(null)} getProductName={getProductName} />
        <PaymentModal show={showPaymentModal} selectedMesa={selectedMesa} total={getCartTotal()} itemCount={getCartCount()} sending={sending} onPay={onPay} onClose={() => setShowPaymentModal(false)} />
        <NoteModal item={noteModal} onAddNote={addNoteToCartItem} onClose={() => setNoteModal(null)} />
        {receiptData && <ReceiptTicketModal saleData={receiptData} onClose={() => setReceiptData(null)} />}
      </div>
    );
  }

  // ==================== DESKTOP / TABLET VIEW ====================
  return (
    <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden" data-testid="manual-order-panel">
      {/* Top Bar */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center gap-3">
          {viewMode === 'pedido' ? (
            <>
              {renderSectionTabs(false)}
              <button onClick={() => setShowMesaSelector(true)}
                className={`flex items-center gap-2 px-4 h-12 rounded-xl font-bold text-base transition-all flex-shrink-0 ${selectedMesa ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid="mesa-selector-btn">
                {selectedMesa ? `Mesa ${selectedMesa}` : 'Sin mesa'}
              </button>
              <div className="relative flex-1 max-w-md ml-auto">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder="Buscar producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 text-base rounded-xl bg-gray-50 border-gray-200" data-testid="search-input" />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <button onClick={() => setShowMesaSelector(true)}
                className={`flex items-center gap-2 px-4 h-12 rounded-xl font-bold text-base transition-all flex-shrink-0 ${selectedMesa ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                data-testid="carta-mesa-selector-btn">
                {selectedMesa ? `Mesa ${selectedMesa}` : 'Sin mesa'}
              </button>
              <p className="text-gray-500 text-sm">Mostrando carta del cliente</p>
            </>
          )}

          {/* Pedido/Carta switch — moved to RIGHT */}
          <div className="flex bg-gray-100 rounded-xl p-1 flex-shrink-0 ml-auto" data-testid="view-mode-switch">
            <button onClick={() => setViewMode('pedido')}
              className={`flex items-center gap-2 px-4 h-10 rounded-lg font-semibold text-sm transition-all ${viewMode === 'pedido' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="mode-pedido-btn">
              <ClipboardList size={18} /> Pedido
            </button>
            <button onClick={() => setViewMode('carta')}
              className={`flex items-center gap-2 px-4 h-10 rounded-lg font-semibold text-sm transition-all ${viewMode === 'carta' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid="mode-carta-btn">
              <BookOpen size={18} /> Carta
            </button>
          </div>

          {viewMode === 'carta' && getCartCount() > 0 && (
            <button onClick={() => setViewMode('pedido')} className="flex items-center gap-2 px-4 h-12 rounded-xl bg-green-500 text-white font-bold active:scale-95 transition-all"
              data-testid="back-to-pedido-btn">
              <ShoppingCart size={20} />
              {getCartCount()} · {getCartTotal().toFixed(2)}€
            </button>
          )}
        </div>
      </div>

      {/* CONTENT */}
      {viewMode === 'carta' ? (
        <div className="flex-1 overflow-hidden bg-stone-100">
          <iframe key={`carta-mesa-${selectedMesa || 1}`} src={`${window.location.origin}/mesa/${selectedMesa || 1}`}
            className="w-full h-full border-0" title="Carta del cliente" data-testid="carta-iframe" />
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Categories sidebar */}
          <div className="w-52 bg-white border-r flex flex-col">
            <ScrollArea className="flex-1">
              <div className="p-3 space-y-1.5">
                {groupedCategories[selectedSection]?.map(cat => {
                  const isActive = selectedCategory === cat;
                  return (
                    <button key={cat} onClick={() => { setSelectedCategory(cat); setSearchQuery(''); }}
                      className={`w-full text-left px-4 py-3.5 rounded-xl text-base font-medium transition-all active:scale-[0.97] ${isActive ? 'text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}`}
                      style={{ backgroundColor: isActive ? SECTION_TABS[selectedSection].color : undefined }}
                      data-testid={`category-btn-${cat}`}>
                      {cat}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Products Grid */}
          <div className="flex-1 bg-gray-50 flex flex-col min-w-0">
            {searchQuery && (
              <div className="px-4 pt-3">
                <p className="text-sm text-gray-500">Resultados para "<span className="font-medium text-gray-700">{searchQuery}</span>"</p>
              </div>
            )}
            <ScrollArea className="flex-1">
              <div className="p-4 grid grid-cols-2 xl:grid-cols-3 gap-3">
                {filteredProducts.map(product => renderProductCard(product, true))}
              </div>
              {filteredProducts.length === 0 && (
                <div className="text-center py-16 text-gray-400">
                  <Search size={40} className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg">No hay productos</p>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Cart Panel */}
          <CartPanel cart={cart} selectedMesa={selectedMesa} getCartTotal={getCartTotal} getCartCount={getCartCount}
            updateQuantity={updateQuantity} updateItemPrice={updateItemPrice} clearCart={clearCart} onNoteClick={setNoteModal} onCobrar={() => setShowPaymentModal(true)}
            onSendToTable={onSendToTable} sending={sending} />
        </div>
      )}

      <SelectionModal product={selectionModal} selectedOptions={selectedOptions} selectedSupplements={selectedSupplements}
        getOptionGroups={getOptionGroups} getGroupLabel={getGroupLabel} getRequiredCount={getRequiredCount}
        onSelectOption={handleSelectOption} onToggleSupplement={handleToggleSupplement}
        isComplete={isSelectionComplete()} onConfirm={handleConfirmSelection} onClose={() => setSelectionModal(null)} getProductName={getProductName} />
      <PaymentModal show={showPaymentModal} selectedMesa={selectedMesa} total={getCartTotal()} itemCount={getCartCount()} sending={sending} onPay={onPay} onClose={() => setShowPaymentModal(false)} />
      <NoteModal item={noteModal} onAddNote={addNoteToCartItem} onClose={() => setNoteModal(null)} />
      <MesaSelector show={showMesaSelector} mesas={mesas} selectedMesa={selectedMesa} onSelect={setSelectedMesa} onClose={() => setShowMesaSelector(false)} />
      {receiptData && <ReceiptTicketModal saleData={receiptData} onClose={() => setReceiptData(null)} />}
    </div>
  );
};

export default ManualOrderPanel;
